import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import type { TaskDto } from '@gr/core/platform/tasks/dto';
import { Link, useT } from '../../../../app/di';
import { IconButton } from '../../../../ui-kit/Button/Button';
import { activeCount, BLOG_TASKS_SECTION } from '../model/task-view';
import { TaskList } from '../TaskList/TaskList';
import styles from './TaskIndicator.module.scss';

export interface TaskIndicatorProps {
  /** Задачи индикатора: блога и, у администратора, платформы (indicatorTasks). */
  tasks: readonly TaskDto[];
  /** Блог для ссылки «Все задачи блога»; null — блог не открыт. */
  blogId: string | null;
  timezone?: string;
  /** Ссылка на раздел администратора. */
  isAdmin: boolean;
  /** С какой стороны раскрывается список: в шапке телефона — вправо, в меню — влево. */
  align?: 'start' | 'end';
  canManage(task: TaskDto): boolean;
  pendingId?: string | null;
  onCancel(task: TaskDto): void;
  onResume(task: TaskDto): void;
}

/** TASK-UC-2: значок фоновых задач в шапке со счётчиком идущих и раскрывающимся списком. */
export function TaskIndicator({ tasks, blogId, timezone, isAdmin, align = 'end', canManage, pendingId, onCancel, onResume }: TaskIndicatorProps): ReactElement {
  const { t, tp } = useT();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const running = activeCount(tasks);

  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent): void => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const label = running > 0 ? tp('tasks.indicator.labelActive', running, { n: running }) : t('tasks.indicator.label');
  return (
    <div className={styles.root} ref={root}>
      <IconButton icon="layers" label={label} className={`${styles.button} ${running > 0 ? styles.busy : ''}`} aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(!open)} />
      {running > 0 ? (
        <span className={styles.badge} aria-hidden="true">
          {running}
        </span>
      ) : null}
      {open ? (
        <div
          id={panelId}
          className={`${styles.panel} ${align === 'start' ? styles.start : styles.end}`}
          role="region"
          aria-label={t('tasks.indicator.title')}
          // Переход по ссылке из списка (отчёт, все задачи) закрывает его.
          onClickCapture={(event) => {
            if (event.target instanceof Element && event.target.closest('a')) setOpen(false);
          }}
        >
          <p className={styles.title}>{t('tasks.indicator.title')}</p>
          <TaskList
            tasks={tasks}
            empty={<p className="u-muted u-small">{t('tasks.indicator.empty')}</p>}
            timezone={timezone}
            showBlog={isAdmin}
            compact
            withReportLinks
            canManage={canManage}
            pendingId={pendingId}
            onCancel={onCancel}
            onResume={onResume}
          />
          <div className={styles.links}>
            {blogId ? (
              <Link to="settings" params={{ blogId, section: BLOG_TASKS_SECTION }}>
                {t('tasks.indicator.allBlog')}
              </Link>
            ) : null}
            {isAdmin ? (
              <Link to="adminTasks">
                {t('tasks.indicator.allPlatform')}
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
